import {
  useAddWorkOrderLabourMutation,
  useUpdateWorkOrderLabourMutation,
} from "../../../store/services/api/work-order-api";
import React, { useRef, useState, useEffect } from "react";
import { Checkbox, Form, Input, InputNumber, Modal } from "antd";
import UserSelect from "../../../components/forms/controls/UserSelect";
import {
  WorkOrderLabour,
  WorkOrderLabourForm,
} from "../../../store/data/entities/work-order-labour";
import AdaptiveDatePicker from "../../../components/form-controls/AdaptiveDatePicker";
import AdaptiveTimePicker from "../../../components/form-controls/AdaptiveTimePicker";
import { DateTime } from "luxon";
import useUser from "../../../hooks/useUser";

interface AddWorkOrderLabourModalProps {
  workOrderId: string;
  technicianId?: string;
  onSuccess?: () => void;
  onClose: () => void;
  visible: boolean;
  labour?: WorkOrderLabour;
}

type LabourFormWithLuxon = Omit<
  WorkOrderLabourForm,
  "date" | "startDateTime" | "endDateTime"
> & {
  date: DateTime;
  startTime: DateTime;
  endTime?: DateTime;
  isThirdParty: boolean;
};

export default function AddWorkOrderLabourModal(
  props: AddWorkOrderLabourModalProps
) {
  const [addLabour, { isLoading: isAdding }] = useAddWorkOrderLabourMutation();
  const [updateLabour, { isLoading: isUpdating }] =
    useUpdateWorkOrderLabourMutation();

  const [form] = Form.useForm<LabourFormWithLuxon>();
  const isThirdParty = Form.useWatch("isThirdParty", form);
  const { user } = useUser();
  const canEdit = user.role === "Admin";
  const isExternal = user.role === "ExternalTechnician";

  if (isExternal) {
    form.setFieldValue("isThirdParty", true);
    form.setFieldValue("thirdPartyName", user.name);
  }

  useEffect(() => {
    form.resetFields();

    if (props.labour) {
      const labour = props.labour;
      form.setFieldsValue({
        userId: labour.userId,
        isThirdParty: !labour.userId,
        thirdPartyName: labour.thirdPartyName,
        date: DateTime.fromISO(labour.date),
        startTime: DateTime.fromISO(labour.startDateTime),
        endTime: labour.endDateTime
          ? DateTime.fromISO(labour.endDateTime)
          : undefined,
        hoursWorked: labour.hoursWorked,
      } as Partial<LabourFormWithLuxon>);
    } else {
      const now = DateTime.now();
      form.setFieldValue("userId", props?.technicianId);
      form.setFieldValue("date", now.startOf("day"));
    }

    if (props.visible) {
      const modalWidth = Math.min(600, window.innerWidth - 24);
      const modalHeight = 400;
      const x = Math.max((window.innerWidth - modalWidth) / 2, 8);
      const y = Math.max((window.innerHeight - modalHeight) / 3, 8);
      setPosition({ x, y });
    }
  }, [props.visible, props.labour, props.technicianId, form]);

  const calculateHoursWorked = (startTime?: DateTime, endTime?: DateTime) => {
  if (startTime && endTime) {
    const normalizedEndTime = endTime.set({
      year: startTime.year,
      month: startTime.month,
      day: startTime.day,
    });

    const durationMinutes = normalizedEndTime
      .diff(startTime, "minutes")
      .minutes;

    return durationMinutes && durationMinutes > 0
      ? (durationMinutes / 60).toFixed(2)
      : 0;
  }

  return 0;
};

  const handleOnSubmit = async (data: LabourFormWithLuxon) => {
    const startDateTime = data.date.set({
      hour: data.startTime.hour,
      minute: data.startTime.minute,
      second: 0,
      millisecond: 0,
    });

    const endDateTime = data.endTime
      ? data.date.set({
          hour: data.endTime.hour,
          minute: data.endTime.minute,
          second: 0,
          millisecond: 0,
        })
      : undefined;

    const payload: WorkOrderLabourForm & { workOrderId: string } = {
      id: props.labour?.id,
      userId: data.isThirdParty ? undefined : data.userId,
      thirdPartyName: data.isThirdParty ? data.thirdPartyName : undefined,
      date: data.date.toUTC().toISO(),
      startDateTime: startDateTime.toUTC().toISO(),
      endDateTime: endDateTime ? endDateTime.toUTC().toISO() : undefined,
      hoursWorked: Number(data.hoursWorked),
      workOrderId: props.workOrderId,
    };

    try {
      if (props.labour) {
        await updateLabour(payload).unwrap();
      } else {
        await addLabour(payload).unwrap();
      }
      props.onSuccess?.();
      props.onClose();
    } catch (err) {
      console.error("Failed to save labour", err);
    }
  };

  const handleValuesChange = (
    changedValues: Partial<LabourFormWithLuxon>,
    allValues: LabourFormWithLuxon
  ) => {
    if ("startTime" in changedValues || "endTime" in changedValues) {
      const { startTime, endTime } = allValues;

      if (endTime == null) {
        form.setFieldValue("hoursWorked", 0);
      } else if (startTime) {
        const calculatedHours = calculateHoursWorked(startTime, endTime);
        form.setFieldValue("hoursWorked", calculatedHours);
      }
    }

    if ("hoursWorked" in changedValues && allValues.startTime) {
      const startTime = allValues.startTime;
      const hours = Number(allValues.hoursWorked);

      if (!isNaN(hours) && hours > 0) {
        const newEndTime = startTime.plus({ hours });
        form.setFieldValue("endTime", newEndTime);
      } else {
        form.setFieldValue("endTime", undefined);
      }
    }
  };

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragData = useRef<{
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
  } | null>(null);

  // --- drag logic copied from AddWorkLogModal ---
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;

    dragData.current = {
      startX: clientX,
      startY: clientY,
      initialX: position.x,
      initialY: position.y,
    };

    document.addEventListener("mousemove", handleDragging);
    document.addEventListener("touchmove", handleDragging as any, { passive: false });
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleDragging = (e: MouseEvent | TouchEvent) => {
    if (!dragData.current) return;

    const clientX = "touches" in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = "touches" in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

    const dx = clientX - dragData.current.startX;
    const dy = clientY - dragData.current.startY;

    setPosition({
      x: dragData.current.initialX + dx,
      y: dragData.current.initialY + dy,
    });

    e.preventDefault();
  };

  const handleDragEnd = () => {
    document.removeEventListener("mousemove", handleDragging);
    document.removeEventListener("touchmove", handleDragging as any);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchend", handleDragEnd);
    dragData.current = null;
  };

  const isLoading = isAdding || isUpdating;

  return (
    <Modal
      open={props.visible}
      title={
        <div
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{
            cursor: "move",
            userSelect: "none",
            padding: "4px 0",
            fontWeight: 600,
          }}
        >
          {props.labour ? "Edit Work Labour" : "Add Work Labour"}
        </div>
      }
      confirmLoading={isLoading}
      onCancel={props.onClose}
      onOk={() => form.submit()}
      okButtonProps={{
        disabled:
          isLoading ||
          (!!props.labour && !!props.labour.endDateTime && !canEdit),
      }}
      destroyOnClose
      centered={false}
      width={Math.min(600, window.innerWidth - 24)}
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        transform: "none",
        margin: 0,
      }}
    >
      <Form
        style={{ maxHeight: "80vh", overflowY: "auto" }}
        form={form}
        onFinish={handleOnSubmit}
        onValuesChange={handleValuesChange}
        labelCol={{ span: 6 }}
      >
        <Form.Item name="isThirdParty" label="External" valuePropName="checked">
          <Checkbox disabled={isLoading || isExternal || (!!props.labour && !canEdit)} />
        </Form.Item>

        <Form.Item
          hidden={!isThirdParty}
          name="thirdPartyName"
          label="External Name"
          rules={[
            { required: isThirdParty, message: "External Name is required" },
          ]}
        >
          <Input disabled={isExternal} />
        </Form.Item>

        <Form.Item
          hidden={isThirdParty}
          name="userId"
          label="Technician"
          rules={[
            { required: !isThirdParty, message: "Technician is required" },
          ]}
        >
          <UserSelect
            userRoles={["Technician"]}
            disabled={isLoading || (!!props.labour && !canEdit)}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="date"
          label="Date"
          rules={[{ required: true, message: "Date is required" }]}
        >
          <AdaptiveDatePicker
            disabled={isLoading || (!!props.labour && !canEdit)}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="startTime"
          label="Start Time"
          rules={[
            { required: true, message: "Start time is required" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const endTime = getFieldValue("endTime");
                if (!value) {
                  return Promise.reject(new Error("Start time is required"));
                }
                if (!endTime || value < endTime) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Start time must be earlier than end time")
                );
              },
            }),
          ]}
        >
          <AdaptiveTimePicker
            disabled={isLoading || (!!props.labour && !canEdit)}
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item name="endTime" label="End Time">
          <AdaptiveTimePicker
            disabled={
              isLoading ||
              (!!props.labour && !!props.labour.endDateTime && !canEdit)
            }
            style={{ width: "100%" }}
          />
        </Form.Item>

        <Form.Item
          name="hoursWorked"
          label="Hours"
          rules={[{ required: true, message: "Hours are required" }]}
        >
          <InputNumber
            disabled={isLoading || (!!props.labour && !canEdit)}
            style={{ width: "100%" }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
